import React from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import ItemPicker from '../components/ItemPicker'
import MoneyInput from '../components/MoneyInput'
import { formatMoney } from '../lib/money'
import { parseQuantity, type DraftPriceItem } from '../lib/priceItems'
import type { CostTree } from '../lib/tree'

interface Props {
  items: DraftPriceItem[]
  /** The draft has rows, so an empty `items` means the filter hides them all. */
  hasDraft: boolean
  finals: Map<string, string>
  tree: CostTree
  selected: ReadonlySet<string>
  allSelected: boolean
  disabled: boolean
  currency: string
  locale: string
  onToggle: (key: string) => void
  onToggleAll: () => void
  onChange: (key: string, patch: Partial<DraftPriceItem>) => void
}

const NO_EXCLUDE: string[] = []

/**
 * The editor's rows: every field editable and the budget item picked per row (2026-10-06 spec §8).
 * Controls carry their own widths: a 100 %-wide table squeezed them to nothing to fit the budget item's full name (Freelo #33146584);
 * now the table scrolls sideways instead.
 * ponytail: a plain table renders ~200 rows fine; virtualize like BudgetTreeGrid above ~1000.
 */
const PriceItemsTable: React.FC<Props> = ({ items, hasDraft, finals, tree, selected, allSelected, disabled, currency, locale, onToggle, onToggleAll, onChange }) => {
  const { t } = useTranslation()
  if (!items.length) {
    return <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">{t(hasDraft ? 'CostControl_price_editor_no_match' : 'CostControl_price_editor_empty')}</p>
  }
  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-md border">
      {/* One scroll box for both axes: the sideways scrollbar stays in view and the header keeps sticking. */}
      <Table containerClassName="overflow-visible">
        <TableHeader className="sticky top-0 z-10 bg-background">
          <TableRow>
            <TableHead className="w-10">
              <Checkbox checked={allSelected} onCheckedChange={onToggleAll} disabled={disabled} aria-label={t('CostControl_price_select_all')} />
            </TableHead>
            <TableHead className="min-w-32">{t('CostControl_price_col_section')}</TableHead>
            <TableHead className="w-24">{t('CostControl_price_col_code')}</TableHead>
            <TableHead className="min-w-56">{t('CostControl_price_col_name')}</TableHead>
            <TableHead className="w-20">{t('CostControl_price_col_unit')}</TableHead>
            <TableHead className="w-24 text-right">{t('CostControl_price_col_quantity')}</TableHead>
            <TableHead className="w-36 text-right">{t('CostControl_price_col_amount')}</TableHead>
            <TableHead className="w-24 text-right">{t('CostControl_price_col_discount')}</TableHead>
            <TableHead className="w-32 text-right">{t('CostControl_price_col_final')}</TableHead>
            <TableHead className="min-w-56">{t('CostControl_price_col_budget_item')}</TableHead>
            <TableHead className="w-20 text-center">{t('CostControl_provisional_switch')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((i) => {
            const node = i.nodeId ? tree.byId.get(i.nodeId)?.activity : undefined
            const badQuantity = parseQuantity(i.quantity) === undefined
            const blankName = !i.name.trim()
            const set = (patch: Partial<DraftPriceItem>) => onChange(i.key, patch)
            return (
              <TableRow key={i.key} data-state={selected.has(i.key) ? 'selected' : undefined}>
                <TableCell>
                  <Checkbox checked={selected.has(i.key)} onCheckedChange={() => onToggle(i.key)} disabled={disabled} aria-label={t('CostControl_price_select_row')} />
                </TableCell>
                <TableCell><Input value={i.section} onChange={(e) => set({ section: e.target.value })} disabled={disabled} className="h-8 w-32" aria-label={t('CostControl_price_col_section')} /></TableCell>
                <TableCell><Input value={i.code} onChange={(e) => set({ code: e.target.value })} disabled={disabled} className="h-8 w-24" aria-label={t('CostControl_price_col_code')} /></TableCell>
                <TableCell>
                  <Input
                    value={i.name}
                    onChange={(e) => set({ name: e.target.value })}
                    disabled={disabled}
                    aria-invalid={blankName || undefined}
                    className={cn('h-8 w-64', blankName && 'border-destructive focus-visible:ring-destructive')}
                    aria-label={t('CostControl_price_col_name')}
                  />
                </TableCell>
                <TableCell><Input value={i.unit} onChange={(e) => set({ unit: e.target.value })} disabled={disabled} className="h-8 w-20" aria-label={t('CostControl_price_col_unit')} /></TableCell>
                <TableCell>
                  <Input
                    value={i.quantity}
                    inputMode="decimal"
                    onChange={(e) => set({ quantity: e.target.value })}
                    disabled={disabled}
                    aria-invalid={badQuantity || undefined}
                    className={cn('h-8 w-24 text-right tabular-nums', badQuantity && 'border-destructive focus-visible:ring-destructive')}
                    aria-label={t('CostControl_price_col_quantity')}
                  />
                </TableCell>
                <TableCell>
                  <MoneyInput value={i.amount} onChange={(amount, valid) => set({ amount, amountValid: valid })} disabled={disabled} className="h-8 w-36" aria-label={t('CostControl_price_col_amount')} />
                </TableCell>
                <TableCell>
                  <MoneyInput value={i.discountPercent} onChange={(discountPercent, valid) => set({ discountPercent, discountValid: valid })} disabled={disabled} className="h-8 w-20" aria-label={t('CostControl_price_col_discount')} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-right tabular-nums">{formatMoney(finals.get(i.key) ?? '0.00', { locale, currency })}</TableCell>
                <TableCell>
                  {/* A table cell ignores max-width; the fixed wrapper is what makes the long item name truncate. */}
                  <div className="w-72">
                    <ItemPicker
                      variant="select"
                      tree={tree}
                      excludeIds={NO_EXCLUDE}
                      disabled={disabled}
                      label={node ? (node.code ? `${node.code} · ${node.name}` : node.name) : undefined}
                      onPick={(nodeId) => set({ nodeId })}
                    />
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Switch checked={i.provisional} onCheckedChange={(provisional) => set({ provisional })} disabled={disabled} aria-label={t('CostControl_provisional_switch')} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
export default PriceItemsTable
